import { Bookmark, Filter, Star } from "lucide-react";

import defaultAvatar from "../../../assets/images/user/user.png";
import { profiles } from "../../../_helper/User/UserData/data";

export const STATUS_OPTIONS = [
  "To Do",
  "In progress",
  "Admin Approval",
  "Customer Approval",
  "Done",
];

// StatusPill colorMap formatına uygun: { value: { bg, fg, border } }
const STATUS_COLORS = {
  "To Do": { fg: "#0c5a91", bg: "rgb(56 164 248 / 12%)", br: "rgb(56 164 248 / 24%)" },
  "In progress": { fg: "#9a6700", bg: "rgb(255 184 41 / 14%)", br: "rgb(255 184 41 / 24%)" },
  "Admin Approval": { fg: "#a01030", bg: "rgb(245 50 92 / 12%)", br: "rgb(245 50 92 / 24%)" },
  "Customer Approval": { fg: "#1a3854", bg: "rgb(44 88 126 / 12%)", br: "rgb(44 88 126 / 24%)" },
  Done: { fg: "#0a7a55", bg: "rgb(10 187 135 / 12%)", br: "rgb(10 187 135 / 24%)" },
};

export const STATUS_COLOR_MAP = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([key, { fg, bg, br }]) => [
    key,
    { fg, bg, border: br },
  ])
);

export const FILTER_ICON_MAP = {
  bookmark: Bookmark,
  filter: Filter,
  star: Star,
};

export const createTaskFilters = () => ({
  status: "",
  project: "",
  milestone: "",
  priority: "",
  label: "",
  relatedTo: "",
  assignedTo: "",
  startDate: null,
  deadline: null,
});

export const createOptions = (tasks, key) =>
  Array.from(new Set((tasks || []).map((task) => task?.[key]).filter(Boolean))).map(
    (value) => ({
      label: value,
      value,
    })
  );

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const source = String(value).trim();
  const firstMatch = source.match(
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
  );

  if (firstMatch) {
    return new Date(
      Number(firstMatch[3]),
      Number(firstMatch[2]) - 1,
      Number(firstMatch[1]),
      Number(firstMatch[4] || 0),
      Number(firstMatch[5] || 0)
    );
  }

  const secondMatch = source.match(
    /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?$/
  );

  if (secondMatch) {
    return new Date(
      Number(secondMatch[1]),
      Number(secondMatch[2]) - 1,
      Number(secondMatch[3]),
      Number(secondMatch[4] || 0),
      Number(secondMatch[5] || 0)
    );
  }

  const parsed = new Date(source);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatFilterDate = (value) => {
  const parsed = toDate(value);
  return parsed ? parsed.toLocaleDateString() : String(value || "");
};

export const areTaskFilterValuesEqual = (leftValue, rightValue) => {
  const leftDate = toDate(leftValue);
  const rightDate = toDate(rightValue);

  if (leftDate || rightDate) {
    if (!leftDate || !rightDate) return false;
    return leftDate.getTime() === rightDate.getTime();
  }

  return String(leftValue || "") === String(rightValue || "");
};

export const areTaskFiltersEqual = (leftFilters = {}, rightFilters = {}) => {
  const filterKeys = Object.keys(createTaskFilters());

  return filterKeys.every((key) =>
    areTaskFilterValuesEqual(leftFilters?.[key], rightFilters?.[key])
  );
};

export const getTaskLabels = (task) => {
  if (Array.isArray(task?.labels)) return task.labels.filter(Boolean);
  if (!task?.labels) return [];

  return String(task.labels)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const TASK_USER_DIRECTORY = profiles
  .filter((profile) => profile?.type === "user" && profile?.overview?.name)
  .map((profile) => ({
    id: String(profile.id),
    name: profile.overview.name,
    avatar: profile.overview.avatar || defaultAvatar,
  }));

export const splitPeopleValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (!value) return [];

  return String(value)
    .split(/[,;\n|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const resolveTaskUsers = (value, idValue) => {
  const ids = splitPeopleValue(idValue);

  if (ids.length) {
    return ids
      .map((id) =>
        TASK_USER_DIRECTORY.find((user) => String(user.id) === String(id))
      )
      .filter(Boolean);
  }

  const names = splitPeopleValue(value);

  return names
    .map((name) =>
      TASK_USER_DIRECTORY.find((user) => user.name === String(name))
    )
    .filter(Boolean);
};

export const getTaskSearchHaystack = (task) => {
  const labels = getTaskLabels(task).join(" ");
  const values = [
    task?.id,
    task?.title,
    task?.description,
    task?.relatedTo,
    task?.project,
    task?.milestone,
    task?.assignedTo,
    task?.collaborators,
    task?.status,
    task?.priority,
    task?.points,
    task?.startDate,
    task?.deadline,
    labels,
  ];

  return values
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const isQuickFilterMatch = (task, quickFilter) => {
  if (!quickFilter?.type || !quickFilter?.value) return true;

  if (quickFilter.type === "label") {
    return getTaskLabels(task).includes(quickFilter.value);
  }

  if (quickFilter.type === "priority") {
    return String(task?.priority || "") === String(quickFilter.value || "");
  }

  if (quickFilter.type === "points") {
    return Number(task?.points) === Number(quickFilter.value);
  }

  return true;
};

export const getTaskComparableValue = (task, key) => {
  switch (key) {
    case "task":
    case "title":
      return task?.title || "";
    case "deadline": {
      const parsed = toDate(task?.deadline);
      return parsed || task?.deadline || "";
    }
    case "startDate": {
      const parsed = toDate(task?.startDate);
      return parsed || task?.startDate || "";
    }
    case "assignedTo":
      return splitPeopleValue(task?.assignedTo).join(", ") || "";
    case "collaborators":
      return splitPeopleValue(task?.collaborators).join(", ") || "";
    case "status":
      return task?.status || "";
    case "relatedTo":
      return task?.relatedTo || "";
    case "milestone":
      return task?.milestone || "";
    default:
      return task?.[key] ?? "";
  }
};

export const compareSmartValues = (leftValue, rightValue) => {
  const normalize = (value) => {
    if (value === null || value === undefined || value === "") {
      return { type: "empty", value: "" };
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return { type: "date", value: value.getTime() };
    }

    const parsed = toDate(value);
    if (parsed) return { type: "date", value: parsed.getTime() };

    const numeric = Number(value);
    if (!Number.isNaN(numeric) && String(value).trim() !== "") {
      return { type: "number", value: numeric };
    }

    return { type: "string", value: String(value).toLowerCase() };
  };

  const left = normalize(leftValue);
  const right = normalize(rightValue);

  if (left.type !== right.type) {
    return left.type.localeCompare(right.type);
  }

  if (left.type === "string" || left.type === "empty") {
    return left.value.localeCompare(right.value);
  }

  return left.value - right.value;
};

export const getActiveFilterEntries = (filters, quickFilter) => {
  const entries = [];

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (!value) return;

    entries.push({
      key,
      label:
        key === "startDate" || key === "deadline"
          ? formatFilterDate(value)
          : String(value),
    });
  });

  if (quickFilter?.type && quickFilter?.value) {
    entries.push({
      key: `quick:${quickFilter.type}`,
      label: String(quickFilter.value),
      quick: true,
      quickType: quickFilter.type,
    });
  }

  return entries;
};
